import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEqual } from 'lodash'
import { apiFetch } from 'utils/apiFetch'
import type {
  TCachedValuesFieldAssetData,
  TFetchedRequestedKeys,
  TFetchValuesDataResult,
  TFieldAsset,
  THasValuesDataArgs,
} from './_types'

type TFieldAssetRequestObject = Extract<
  NonNullable<routes.Values.Request[keyof routes.Values.Request]>,
  { valuesRequested: unknown }
>

const getIdsFromFieldAssetRequestObject = (
  fieldAssetRequestObject: TFieldAssetRequestObject,
): (string | number)[] | null => {
  if ('lngLats' in fieldAssetRequestObject) return fieldAssetRequestObject.lngLats

  if ('blockIds' in fieldAssetRequestObject) return fieldAssetRequestObject.blockIds

  if ('propertyIds' in fieldAssetRequestObject) return fieldAssetRequestObject.propertyIds

  if ('irrigationZoneIds' in fieldAssetRequestObject) return fieldAssetRequestObject.irrigationZoneIds

  if ('regionIds' in fieldAssetRequestObject) return fieldAssetRequestObject.regionIds

  return null
}

const freshnessTTLMilliseconds = 5 * 60 * 1000 // five minutes

const doWeHaveCachedDataAndIsItFresh = (args: THasValuesDataArgs): boolean => {
  const { ids, fieldAssetType, valuesRequested, fetchedRequestedKeys } = args

  if (ids.length === 0) return true

  const requestedKeys = Object.keys(valuesRequested)

  if (requestedKeys.length === 0) return true

  const fetchedEntry = fetchedRequestedKeys[fieldAssetType]

  if (!fetchedEntry) return false

  const staleCutoffTimestamp = Date.now() - freshnessTTLMilliseconds

  return requestedKeys.every((valueKey) => {
    const fetchedValue = fetchedEntry[valueKey]

    if (!fetchedValue || !isEqual(fetchedValue.config, valuesRequested[valueKey])) return false

    return ids.every((id) => {
      const fetchedTime = fetchedValue.fetchedIds[String(id)]

      return fetchedTime !== undefined && fetchedTime >= staleCutoffTimestamp
    })
  })
}

const mergeFieldAssetValues = (fieldAsset1: TFieldAsset, fieldAsset2: TFieldAsset): TFieldAsset => {
  return Object.entries(fieldAsset2).reduce(
    (mergedFieldAssetType, [fieldAssetId, newFieldAsset]) => {
      const existingFieldAsset = mergedFieldAssetType[fieldAssetId]

      if (existingFieldAsset) {
        const updatedValues = {
          ...existingFieldAsset.values,
          ...(newFieldAsset.values || {}),
        }

        return { ...mergedFieldAssetType, [fieldAssetId]: { ...existingFieldAsset, values: updatedValues } }
      } else {
        return { ...mergedFieldAssetType, [fieldAssetId]: newFieldAsset }
      }
    },
    { ...fieldAsset1 },
  )
}

const mergeData = (prevData: TCachedValuesFieldAssetData, newData: TCachedValuesFieldAssetData) => {
  return (Object.keys(newData) as (keyof TCachedValuesFieldAssetData)[]).reduce(
    (prev, fieldAssetType) => {
      return {
        ...prev,
        [fieldAssetType]: mergeFieldAssetValues(
          (prev[fieldAssetType] ?? {}) as TFieldAsset,
          (newData[fieldAssetType] ?? {}) as TFieldAsset,
        ),
      }
    },
    { ...prevData },
  )
}

const cloneFetchedRequestedKeys = (fetchedRequestedKeys: TFetchedRequestedKeys): TFetchedRequestedKeys => {
  const cloned: TFetchedRequestedKeys = {}

  Object.entries(fetchedRequestedKeys).forEach(([fieldAssetType, fetchedEntry]) => {
    cloned[fieldAssetType] = {}

    Object.entries(fetchedEntry).forEach(([valueKey, fetchedValue]) => {
      cloned[fieldAssetType][valueKey] = {
        config: fetchedValue.config,
        fetchedIds: { ...fetchedValue.fetchedIds },
      }
    })
  })

  return cloned
}

export const fetchValuesData = async (
  requestBody: routes.Values.Request,
  locallyCachedValuesData: TCachedValuesFieldAssetData = {},
  fetchedRequestedKeys: TFetchedRequestedKeys = {},
): Promise<TFetchValuesDataResult> => {
  const valuesToRequest: Partial<Record<keyof routes.Values.Request, unknown>> = {}
  const { dateFrom, dateTo, featureReferer, includeSource, ...rest } = requestBody

  const updatedFetchedRequestedKeys =
    Object.keys(locallyCachedValuesData).length === 0 ? {} : cloneFetchedRequestedKeys(fetchedRequestedKeys)

  Object.entries(rest).forEach(([fieldAssetType, fieldAssetRequestObject]) => {
    const { valuesRequested } = fieldAssetRequestObject
    const ids = getIdsFromFieldAssetRequestObject(fieldAssetRequestObject)

    const weHaveThisDataAndItsFresh =
      ids !== null &&
      doWeHaveCachedDataAndIsItFresh({
        ids,
        fieldAssetType: fieldAssetType as keyof TCachedValuesFieldAssetData,
        valuesRequested,
        fetchedRequestedKeys: updatedFetchedRequestedKeys,
      })

    if (!weHaveThisDataAndItsFresh) {
      valuesToRequest[fieldAssetType as keyof routes.Values.Request] = fieldAssetRequestObject
    }
  })

  if (Object.keys(valuesToRequest).length !== 0) {
    const requestBodyToSend: routes.Values.Request = { ...requestBody }

    Object.keys(rest).forEach((fieldAssetType) => {
      if (!valuesToRequest[fieldAssetType as keyof routes.Values.Request]) {
        delete requestBodyToSend[fieldAssetType as keyof typeof rest]
      }
    })

    const res = await apiFetch({ url: '/values', body: requestBodyToSend })

    if (typeof res === 'string') throw new Error(res)

    const lastFetchedTime = Date.now()

    Object.entries(rest).forEach(([fieldAssetType, fieldAssetRequestObject]) => {
      // stamps must only be written for field asset types that were actually sent,
      // otherwise a fresh type's TTL would be extended without ever refetching it
      if (!valuesToRequest[fieldAssetType as keyof routes.Values.Request]) return

      const { valuesRequested } = fieldAssetRequestObject
      const ids = getIdsFromFieldAssetRequestObject(fieldAssetRequestObject) ?? []

      if (!updatedFetchedRequestedKeys[fieldAssetType]) {
        updatedFetchedRequestedKeys[fieldAssetType] = {}
      }

      Object.entries(valuesRequested).forEach(([valueKey, config]) => {
        if (
          !updatedFetchedRequestedKeys[fieldAssetType][valueKey] ||
          !isEqual(updatedFetchedRequestedKeys[fieldAssetType][valueKey].config, config)
        ) {
          updatedFetchedRequestedKeys[fieldAssetType][valueKey] = { config, fetchedIds: {} }
        }

        ids.forEach((id) => {
          // stamp every requested id, including ones the response returned no data for;
          // the response is the server's answer for everything requested, and "no data"
          // is a cacheable answer for the TTL, otherwise any value key that legitimately
          // has no data keeps the whole request permanently stale
          updatedFetchedRequestedKeys[fieldAssetType][valueKey].fetchedIds[String(id)] = lastFetchedTime
        })
      })
    })

    return { data: mergeData(locallyCachedValuesData, res), updatedFetchedRequestedKeys, didFetch: true }
  }

  return { data: locallyCachedValuesData, updatedFetchedRequestedKeys, didFetch: false }
}
